import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import Card from '@/components/ui/Card';
import Skeleton from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';

/**
 * Standard container for a dashboard-home widget: a Card with a consistent
 * header (icon + title + optional "view all" link) and built-in loading /
 * empty / error states, so every widget looks and behaves the same. Widgets
 * pass their fetched state in and render their content as children.
 */
export interface WidgetShellProps {
  title: string;
  /** Small icon shown left of the title. */
  icon?: ReactNode;
  /** In-app path for the header "view all" link (usually the detail page). */
  to?: string;
  /** Label for the header link. Defaults to "View all". */
  linkLabel?: string;
  /** Optional extra header control rendered before the link (e.g. a toggle). */
  action?: ReactNode;
  isLoading?: boolean;
  /** Truthy → render the error state; string/Error message is surfaced. */
  error?: unknown;
  isEmpty?: boolean;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  /** Extra classes for the loading Skeleton (e.g. its height). */
  skeletonClassName?: string;
  className?: string;
  children?: ReactNode;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Something went wrong loading this widget.';
}

export default function WidgetShell({
  title,
  icon,
  to,
  linkLabel = 'View all',
  action,
  isLoading,
  error,
  isEmpty,
  emptyMessage = 'Nothing to show yet.',
  emptyIcon,
  skeletonClassName = 'h-28',
  className,
  children,
}: WidgetShellProps) {
  return (
    <Card padding="none" className={cn('flex h-full flex-col', className)}>
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        {icon && (
          <span className="text-stone-500 [&>svg]:h-4 [&>svg]:w-4" aria-hidden="true">
            {icon}
          </span>
        )}
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <div className="ml-auto flex items-center gap-2">
          {action}
          {to && (
            <Link
              to={to}
              className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-900"
            >
              {linkLabel}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>

      <div className="flex-1 p-4">
        {isLoading ? (
          <Skeleton className={skeletonClassName} />
        ) : error ? (
          <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{errorMessage(error)}</span>
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center py-8 text-center text-stone-500">
            {emptyIcon && (
              <span className="mb-2 text-stone-400 [&>svg]:h-8 [&>svg]:w-8" aria-hidden="true">
                {emptyIcon}
              </span>
            )}
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          children
        )}
      </div>
    </Card>
  );
}
